import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n";

const STATUS_LABEL = {
  published: "Published", active: "Active", completed: "Completed",
};

export default function CampaignCard({ c }) {
  const { t } = useI18n();
  const canSupport = c.fundraising_enabled && c.payment_enabled;
  const pct = c.target_amount > 0 ? Math.min(100, Math.round(((c.raised_amount || 0) / c.target_amount) * 100)) : 0;

  return (
    <div data-testid={`campaign-card-${c.slug}`} className="group flex flex-col overflow-hidden rounded-2xl border border-emerald-900/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:hh-shadow-lg">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={c.images?.[0] || c.image} alt={c.title} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent" />
        <span className="absolute start-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-900">{c.category?.replace("-", " ")}</span>
        <span className="absolute end-3 top-3 rounded-full bg-emerald-900/85 px-3 py-1 text-[11px] font-semibold text-emerald-50">{STATUS_LABEL[c.status] || c.status}</span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <MapPin className="h-3.5 w-3.5 text-gold" /> {c.location}, {c.country}
        </div>
        <h3 className="mt-2 font-display text-2xl font-bold leading-snug text-emerald-900">{c.title}</h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-600">{c.description}</p>

        {c.partner && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> In partnership with {c.partner}
          </div>
        )}

        {canSupport && c.target_amount > 0 && (
          <div className="mt-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-emerald-900/10">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-700 to-gold" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-1.5 flex justify-between text-xs font-medium text-slate-500">
              <span>{c.currency} {(c.raised_amount || 0).toLocaleString()} raised</span>
              <span>Target {c.currency} {c.target_amount.toLocaleString()}</span>
            </div>
          </div>
        )}

        <div className="mt-5 flex-1" />
        <Link
          to={`/initiatives/${c.slug}`}
          data-testid={`campaign-cta-${c.slug}`}
          className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition ${
            canSupport ? "bg-emerald-900 text-white hover:bg-emerald-800" : "border border-emerald-900/20 text-emerald-900 hover:bg-emerald-50"
          }`}
        >
          {canSupport ? t("cta_support_this") : t("cta_learn")}
          <ArrowRight className="h-4 w-4 rtl:rotate-180" />
        </Link>
      </div>
    </div>
  );
}
